import { useEffect, useState } from "react";
import { Star, Check, Trash2, Loader2, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const Stars = ({ n }) => (
  <span className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i < n ? "fill-gold text-gold" : "text-taupe/25"}`} strokeWidth={1} />
    ))}
  </span>
);

export const ReviewsAdmin = () => {
  const [reviews, setReviews] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data.reviews)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/reviews/${id}/status`, { status: "approved" });
      toast.success("Avis publié sur le site");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis définitivement ?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Avis supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  const pending = (reviews || []).filter((r) => r.status === "pending");
  const approved = (reviews || []).filter((r) => r.status === "approved");

  const Row = ({ r, isPending }) => (
    <div className={`border rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4 justify-between ${isPending ? "bg-gold/8 border-gold/40" : "bg-white border-taupe/20"}`} data-testid={`review-row-${r.id}`}>
      <div className="min-w-[240px] max-w-xl">
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-espresso">{r.name}</p>
          <Stars n={r.rating} />
          {r.service_name && <span className="text-[10px] tracking-[0.1em] uppercase text-taupe-dark">{r.service_name}</span>}
        </div>
        <p className="text-sm text-espresso/70 font-light mt-1.5 leading-relaxed">“{r.text}”</p>
        <p className="text-[10px] text-espresso/40 mt-1.5">{new Date(r.created_at).toLocaleDateString("fr-BE")}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        {isPending && (
          <button
            data-testid={`review-approve-${r.id}`}
            disabled={busyId === r.id}
            onClick={() => approve(r.id)}
            className="rounded-full bg-[#4A6B53] text-white px-4 py-2 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-1.5 hover:brightness-110 transition-[filter] duration-300 disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" /> Valider
          </button>
        )}
        <button
          data-testid={`review-delete-${r.id}`}
          disabled={busyId === r.id}
          onClick={() => remove(r.id)}
          className="rounded-full border border-[#9E4747]/40 text-[#9E4747] px-4 py-2 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-1.5 hover:bg-[#9E4747]/5 transition-colors duration-300 disabled:opacity-50"
        >
          <Trash2 className="w-3.5 h-3.5" /> Supprimer
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-reviews-tab">
      <p className="text-sm text-espresso/60 font-light max-w-2xl">
        Les visiteuses peuvent laisser un avis sur le site. L'avis n'est visible que par son auteure tant que vous ne l'avez pas validé ici — une fois validé, il est public pour toutes.
      </p>
      {!reviews ? (
        <div className="flex items-center gap-3 text-sm text-espresso/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <>
          <div>
            <h3 className="font-display text-2xl text-espresso mb-4">En attente de validation ({pending.length})</h3>
            <div className="space-y-3" data-testid="reviews-pending-list">
              {pending.length === 0 ? (
                <div className="text-center py-10 text-espresso/45 bg-white border border-taupe/20 rounded-2xl" data-testid="reviews-pending-empty">
                  <MessageSquareQuote className="w-8 h-8 mx-auto mb-3 text-taupe/50" strokeWidth={1.5} />
                  <p className="text-sm">Aucun avis en attente.</p>
                </div>
              ) : (
                pending.map((r) => <Row key={r.id} r={r} isPending />)
              )}
            </div>
          </div>
          <div>
            <h3 className="font-display text-2xl text-espresso mb-4">Publiés sur le site ({approved.length})</h3>
            <div className="space-y-3" data-testid="reviews-approved-list">
              {approved.length === 0 ? (
                <p className="text-sm text-espresso/45 py-4" data-testid="reviews-approved-empty">Aucun avis publié pour le moment.</p>
              ) : (
                approved.map((r) => <Row key={r.id} r={r} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
